import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { omit } from 'lodash-es';
import { BehaviorSubject, EMPTY, Observable, Subject, catchError, map, of, switchMap, throwError } from 'rxjs';

import { environment } from '@env';
import {
  LinxBusinessCustomerSteps,
  LinxCounterpartySteps,
  LinxIndividualCustomerSteps,
  LinxStatus,
  LinxStep,
  constants,
} from '@shared/constants/app-constants';
import {
  AttachmentDocument,
  DeclineRequestParams,
  DocumentCreateModel,
  LinxBranding,
  LinxIDCollectionAccount,
  LinxProgressStep,
  LinxRequestStepFormData,
  LinxRequestStepResponse,
  LinxWorkflowRawResponse,
  LinxWorkflowStepRaw,
  StepPollResult,
  WorkflowConsent,
  WorkflowConsentContent,
  WorkflowGate,
} from '@shared/models';
import { isRecoverableCustomFieldError, isWorkflowNoLongerActive } from '@shared/utils';

import { BackendService } from '../backend.service';
import { NotificationService } from '../notification/notification.service';
import { RegionContextService } from '../region-context/region-context.service';
import { StorageService } from '../storage/storage.service';
import { ThemeService } from '../theme/theme.service';

@Injectable({
  providedIn: 'root',
})
export class WorkflowService {
  private backendService = inject(BackendService);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private http = inject(HttpClient);
  private themeService = inject(ThemeService);
  private notificationService = inject(NotificationService);
  private regionContext = inject(RegionContextService);

  stepperState$ = new BehaviorSubject<LinxWorkflowStepRaw[]>([]);
  steps: LinxProgressStep[] = [];
  gates: WorkflowGate[] = [];
  businessName = '';
  branding?: LinxBranding;
  customerType?: string;

  refreshCollectDocuments = new Subject<boolean>();
  refreshSignHere = new Subject<boolean>();

  getWorkflowId(): string | undefined {
    return this.storageService.getItem<string>(constants.WORKFLOW_ID);
  }

  public fetchWorkflowDetails(id: string): Observable<LinxWorkflowRawResponse> {
    const storedWorkflowId = this.storageService.getItem<string>(constants.WORKFLOW_ID);
    const requestId = id ?? storedWorkflowId;
    if (!requestId) {
      return throwError(() => new Error('Error'));
    }
    return this.backendService.get<LinxWorkflowRawResponse>(`${environment.linxV2Endpoint}/${requestId}`).pipe(
      map((response) => {
        const { workflowId, steps, gates = [], businessOperatingName = '', branding } = response;
        this.storageService.setItem(constants.WORKFLOW_ID, workflowId);
        this.steps = steps;
        this.gates = gates;
        this.businessName = businessOperatingName;
        this.branding = branding;
        this.themeService.applyBranding(branding);
        this.regionContext.set(response);
        this.customerType = this.getCustomerType(steps);
        return response;
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  hasOpenGate(gates: WorkflowGate[]): boolean {
    return gates.some((gate) => gate.status === 'OPEN');
  }

  getCustomerType(steps: LinxWorkflowStepRaw[]): string | undefined {
    const isIndividualCustomer = steps.some((step) => LinxIndividualCustomerSteps.includes(step.stepName));
    const isBusinessCustomer = steps.some((step) => LinxBusinessCustomerSteps.includes(step.stepName));
    const isCounterparty = steps.some((step) => LinxCounterpartySteps.includes(step.stepName));
    if (isCounterparty) return 'counterparty';
    return isIndividualCustomer ? 'individual' : isBusinessCustomer ? 'business' : undefined;
  }

  public fetchWorkflowStepDetails(): Observable<LinxWorkflowStepRaw> {
    const workflowId = this.storageService.getItem<string>(constants.WORKFLOW_ID);
    if (!workflowId) {
      return throwError(() => new Error('Error'));
    }
    return this.backendService.get<LinxWorkflowStepRaw>(`${environment.linxV2Endpoint}/${workflowId}/steps/current`).pipe(
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public declineWorkflowStep(formData: DeclineRequestParams): Observable<LinxWorkflowStepRaw> {
    const workflowId = this.storageService.getItem<string>(constants.WORKFLOW_ID);
    if (!workflowId) {
      return throwError(() => new Error('Error'));
    }
    return this.backendService
      .post<LinxWorkflowStepRaw>(`${environment.linxV2Endpoint}/${workflowId}/steps/current/decline`, formData)
      .pipe(
        map((response) => response),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  /**
   * Records the landing-page consents the customer ticked (KOR-15237). Sends back exactly what the page showed;
   * who the customer is, the workflow and the IP are taken by the backend. Errors are left to the caller: an
   * expired token (`presentation-token-invalid`) or a consent still owed (`landing-consents-required`) means the
   * page fetches a fresh set, anything else is a retry.
   */
  public recordConsents(workflowId: string, consents: WorkflowConsent[], location?: GeolocationCoordinates): Observable<unknown> {
    let headers = new HttpHeaders({});
    if (location) {
      headers = headers
        .set('Linx-Latitude', location.latitude.toString())
        .set('Linx-Longitude', location.longitude.toString())
        .set('Linx-Geo-Accuracy', location.accuracy.toString());
    }
    const shown = ({ documentId, version }: WorkflowConsentContent): { documentId: string; version: string } => ({ documentId, version });
    const body = {
      placementKey: 'landing',
      consents: consents.map(({ consentDefinitionId, presentationToken, checkboxText, notice, documents }) => ({
        consentDefinitionId,
        presentationToken,
        language: checkboxText.language,
        checkboxText: shown(checkboxText),
        ...(notice ? { notice: shown(notice) } : {}),
        documents: (documents ?? []).map(shown),
      })),
    };
    return this.backendService.post(`${environment.linxV2Endpoint}/${workflowId}/consents`, body, { headers });
  }

  public submitWorkflowStep(
    formData: LinxRequestStepFormData,
    stepName?: LinxStep,
    location?: GeolocationCoordinates,
  ): Observable<LinxRequestStepResponse> {
    const workflowId = this.storageService.getItem<string>(constants.WORKFLOW_ID);
    if (!workflowId) {
      return throwError(() => new Error('Error'));
    }

    let headers = new HttpHeaders({});
    if (location) {
      headers = headers.set('Linx-Latitude', location.latitude.toString());
      headers = headers.set('Linx-Longitude', location.longitude.toString());
    }

    return this.backendService
      .post<LinxRequestStepResponse>(`${environment.linxV2Endpoint}/${workflowId}/steps/current`, this.withStepId(formData, stepName), {
        headers,
      })
      .pipe(
        map((response) => {
          const { successful } = response;
          if (!successful) {
            this.redirectToFailedWorkflow();
            return response;
          }
          if (stepName && ((successful && !this.isBeneficiaryStep(stepName)) || (this.isBeneficiaryStep(stepName) && formData.continue))) {
            const stepIndex = this.steps.findIndex((step) => step.stepName === stepName && step.status !== 'COMPLETED');
            if (stepIndex !== -1) {
              this.steps[stepIndex].status = 'COMPLETED';
            }
          }
          return response;
        }),
        // A custom field rejection is fixable on the step the customer is already on, so it is handed
        // back to them instead of ending the workflow. Everything else stays terminal: this path is
        // shared by every step, and a wider escape would turn real failures into silent no-ops.
        catchError((errorRes) => {
          if (isRecoverableCustomFieldError(errorRes)) {
            return throwError(() => errorRes);
          }

          // The step is already done - the backend completes a ready IDV step itself on a terminal
          // provider outcome and often wins the race against the customer's own continue. Nothing
          // failed, so land on whatever the workflow actually is instead of reporting a failure at
          // the end of a flow that succeeded.
          if (isWorkflowNoLongerActive(errorRes)) {
            this.redirectToResolvedStatus();
            return EMPTY;
          }

          this.redirectToFailedWorkflow();
          return EMPTY;
        }),
      );
  }

  public pollCurrentWorkflowStep(stepName?: LinxStep): Observable<StepPollResult> {
    const workflowId = this.storageService.getItem<string>(constants.WORKFLOW_ID);
    if (!workflowId) {
      return throwError(() => new Error('Error'));
    }

    return this.backendService.post<StepPollResult>(`${environment.linxV2Endpoint}/${workflowId}/steps/current/poll`).pipe(
      map((response) => {
        if (stepName && response.status === 'COMPLETED') {
          const stepIndex = this.steps.findIndex((step) => step.stepName === stepName && step.status !== 'COMPLETED');
          if (stepIndex !== -1) {
            this.steps[stepIndex].status = 'COMPLETED';
          }
        }
        return response;
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  /**
   * Where a customer belongs once the server reports the step they were completing is already done.
   * Only the workflow can say how it ended, so ask it: still running means there is another step to
   * go to, anything else is an ending the inactive page already knows how to render.
   *
   * A probe that cannot answer still must not produce a failure - the submit has already told us the
   * workflow is no longer active, so the flow is over either way and COMPLETED is the honest ending.
   */
  private redirectToResolvedStatus(): void {
    const workflowId = this.getWorkflowId();
    if (!workflowId) {
      this.redirectToCompletedWorkflow();
      return;
    }

    this.fetchWorkflowDetails(workflowId).subscribe({
      next: (response) => {
        if (response.status === LinxStatus.ACTIVE) {
          this.navigateToNextStep();
          return;
        }
        this.redirectToInactiveRequest(response.status, workflowId);
      },
      error: () => this.redirectToCompletedWorkflow(),
    });
  }

  private redirectToCompletedWorkflow(): void {
    this.redirectToInactiveRequest(LinxStatus.COMPLETED);
  }

  redirectToFailedWorkflow(): void {
    this.redirectToInactiveRequest(LinxStatus.FAILED);
  }

  /**
   * The inactive page lives under its workflow, so without a stored id there is no page to show; 404 matches what
   * every other entry point does without an id. (`Router.navigate` would throw on the `undefined` segment.)
   */
  private redirectToInactiveRequest(error: string, workflowId = this.getWorkflowId()): void {
    if (!workflowId) {
      void this.router.navigate(['404']);
      return;
    }
    void this.router.navigate(['linx-request', workflowId, 'inactive-request'], { state: { error } });
  }

  /**
   * A copy of a step submit's body naming the step the customer is on as far as this app knows - the
   * one navigateToNextStep opens - as `stepId`. KOR refuses the submit with `status-invalid` once that
   * step is no longer current, rather than running whichever step the workflow has moved on to
   * (KOR-15145). A submit that names a different step gets no id: that id could never be current, so
   * every retry would be refused and routed straight back here.
   */
  private withStepId<T extends object>(body: T, stepName?: LinxStep): T | (T & { stepId: string }) {
    const currentStep = this.steps.find((step) => step.status !== 'COMPLETED');
    if (!currentStep?.stepId || (stepName && currentStep.stepName !== stepName)) {
      return body;
    }
    return { ...body, stepId: currentStep.stepId };
  }

  isBeneficiaryStep(stepName: LinxStep): boolean {
    return [LinxStep.CreateBeneficiary, LinxStep.EnhanceBeneficiaryProfile].includes(stepName);
  }

  navigateToNextStep(): void {
    const current = this.steps.find((step) => step.status !== 'COMPLETED');
    const workflowId = this.getWorkflowId();
    if (!workflowId) {
      void this.router.navigate(['404']);
      return;
    }
    if (current) {
      const currentStep = current.stepName;
      this.setActiveStep(current);
      const currentStepUrl = [
        LinxStep.SelectCountry,
        LinxStep.DocumentAcceptance,
        LinxStep.CollectDocuments,
        LinxStep.SignHere,
        LinxStep.CreateFinancialAccount,
        LinxStep.SelectOrCreateFinancialAccount,
        LinxStep.LinkOrCreateFinancialAccount,
        LinxStep.LookupOrVerifyFinancialAccount,
        LinxStep.MoveMoney,
        LinxStep.CreateDebitAuthorization,
        LinxStep.LookupOrCreateDebitAuthorization,
      ].includes(currentStep)
        ? currentStep
        : `${this.customerType}/${currentStep}`;
      this.router.navigateByUrl(`linx-request/${workflowId}/${currentStepUrl}`);
      if (currentStep === LinxStep.CollectDocuments) {
        this.refreshCollectDocuments.next(true);
      }
      if (currentStep === LinxStep.SignHere) {
        this.refreshSignHere.next(true);
      }
      return;
    }
    // Every locally-known step is COMPLETED, but that alone no longer means the workflow is done
    // (KOR-14995): it can stay ACTIVE behind an open completeness gate (e.g. a secondary e-signer
    // still pending). The locally cached gates predate whichever step just completed, so re-fetch
    // before deciding - a stale "no gates" read here would show the customer a false success page.
    this.fetchWorkflowDetails(workflowId).subscribe({
      next: (response) => this.routeForWorkflowStatus(response, workflowId),
      error: () => this.redirectToInactiveRequest(LinxStatus.SUCCESS, workflowId),
    });
  }

  /**
   * Decides where a customer belongs given a freshly-fetched workflow snapshot, once they are no
   * longer sitting on a specific interactive step. Shared between {@link navigateToNextStep}'s
   * terminal branch and the waiting-for-signatures screen's poll loop so the two never disagree.
   */
  routeForWorkflowStatus(response: LinxWorkflowRawResponse, workflowId: string): void {
    if (response.status !== LinxStatus.ACTIVE) {
      this.redirectToInactiveRequest(response.status, workflowId);
      return;
    }
    if (this.hasOpenGate(response.gates ?? [])) {
      this.router.navigate(['linx-request', workflowId, 'waiting-for-signatures']);
      return;
    }
    this.redirectToInactiveRequest(LinxStatus.SUCCESS, workflowId);
  }

  /**
   * Marks the given step instance, not its name: a workflow can repeat a step (two SignHere), and
   * matching by name lit up every instance as current (KOR-15191).
   */
  setActiveStep(activeStep: LinxWorkflowStepRaw): void {
    const stepperState = this.steps.map((step) => {
      return {
        ...step,
        isActiveStep: step === activeStep,
      };
    });
    this.stepperState$.next(stepperState);
  }

  createDocument(documentData: DocumentCreateModel): Observable<LinxIDCollectionAccount[] | undefined> {
    const workflowId = this.storageService.getItem<string>(constants.WORKFLOW_ID);
    if (!workflowId) {
      return throwError(() => new Error('Error'));
    }
    return this.backendService
      .post<LinxRequestStepResponse>(
        `${environment.linxV2Endpoint}/${workflowId}/steps/current`,
        this.withStepId(omit(documentData, ['file', 'backFile'])),
      )
      .pipe(
        switchMap((response) => {
          const { uploadUrl = '', backUploadUrl = '', accounts } = response.entries ?? {};
          // Without an upload URL the file would go nowhere; fail loudly, as for a missing back image URL below.
          if (!uploadUrl) {
            return throwError(() => new Error('Missing upload URL'));
          }
          return this.uploadDocumentFile({ url: uploadUrl, file: documentData.file as string, extension: documentData.fileExtension }).pipe(
            switchMap(() => {
              // Two-sided documents (e.g. driver's licenses) also upload the back image.
              if (!documentData.backFile) {
                return of(true);
              }
              // Back image was provided but the backend gave no upload URL — fail loudly rather than silently dropping it.
              if (!backUploadUrl) {
                return throwError(() => new Error('Missing back image upload URL'));
              }
              return this.uploadDocumentFile({
                url: backUploadUrl,
                file: documentData.backFile as string,
                extension: documentData.backFileExtension,
              });
            }),
            map(() => accounts),
          );
        }),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  uploadDocumentFile({ url, file, extension = '' }: { url: string; file: string; extension?: string }): Observable<boolean> {
    const contentTypes: Record<string, string> = {
      JPG: 'image/jpeg',
      JPEG: 'image/jpeg',
      PNG: 'image/png',
      PDF: 'application/pdf',
    };

    const contentType = contentTypes[extension.toUpperCase()];
    const requestHeaders = {
      headers: contentType ? new HttpHeaders({ 'Content-Type': contentType }) : new HttpHeaders(),
    };

    return this.backendService.upload(url, file, requestHeaders).pipe(
      map(() => true),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  downloadAttachment(attachment: AttachmentDocument): void {
    this.http.get(attachment.url, { responseType: 'blob' }).subscribe({
      next: (file: Blob) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(file);
        a.href = objectUrl;
        a.download = attachment.name ?? 'attachment';
        a.click();
        URL.revokeObjectURL(objectUrl);
      },
      error: () =>
        this.notificationService.displayError(`We could not download ${attachment.name ?? 'this attachment'}. Please try again.`),
    });
  }
}
